/**
 * TodayView (P8) — Slim daily focus.
 *
 * IA cleanup: earnings overlay extracted to EarningsCard (collapsed on Home).
 * Inline notices extracted to ContractorPortal.tsx Home tab.
 *
 * Read-only. No mutations. payoutAmountSnapshot only.
 */

import { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Sun, ChevronRight, MapPin, CheckCircle2 } from "lucide-react";
import { useAppData } from "@/data/AppDataContext";
import { useCurrentContractor } from "@/hooks/useCurrentContractor";
import {
  filterContractorJobs,
  filterContractorPendingOffers,
  resolveJobPayoutSnapshot,
  getJobCompletionDate,
} from "@/data/contractor/commandCenter";

interface TodayViewProps {
  onOpenPendingOffer?: () => void;
}

export function TodayView({ onOpenPendingOffer }: TodayViewProps) {
  const { jobs, marketplaceOffers } = useAppData();
  const { id: contractorId, matchKey } = useCurrentContractor();

  const myJobs = useMemo(
    () => filterContractorJobs(jobs, contractorId, matchKey),
    [jobs, contractorId, matchKey],
  );
  const myPending = useMemo(
    () => filterContractorPendingOffers(marketplaceOffers, contractorId),
    [marketplaceOffers, contractorId],
  );

  const todayIso = new Date().toISOString().slice(0, 10);

  const todaysJobs = useMemo(
    () =>
      myJobs.filter(
        (j) => !getJobCompletionDate(j) && j.scheduledInstallDate === todayIso,
      ),
    [myJobs, todayIso],
  );

  const nextJob = useMemo(() => {
    return myJobs
      .filter(
        (j) =>
          !getJobCompletionDate(j) &&
          j.scheduledInstallDate &&
          j.scheduledInstallDate > todayIso,
      )
      .sort((a, b) =>
        a.scheduledInstallDate.localeCompare(b.scheduledInstallDate),
      )[0];
  }, [myJobs, todayIso]);

  return (
    <Card data-testid="today-view">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-2">
            <Sun className="h-4 w-4" /> Today
          </h2>
          <span className="text-[10px] text-muted-foreground">
            {new Date().toLocaleDateString(undefined, {
              weekday: "long",
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>

        {/* Today's jobs */}
        <div>
          {todaysJobs.length === 0 ? (
            <p className="text-xs text-muted-foreground py-1.5">
              No jobs scheduled for today — enjoy the morning.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {todaysJobs.map((j) => (
                <li
                  key={j.id}
                  data-testid="today-job"
                  className="flex items-center justify-between rounded-md border border-border/50 px-2.5 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-semibold truncate">
                      {j.customerName}
                    </p>
                    <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                      <MapPin className="h-3 w-3" /> {j.address?.city ?? ""}
                    </p>
                  </div>
                  <span className="text-[11px] font-medium">
                    ${resolveJobPayoutSnapshot(j, marketplaceOffers).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Next job + pending offers */}
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-md border border-border/50 p-2.5">
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
              Next Job
            </p>
            {nextJob ? (
              <>
                <p className="text-xs font-semibold truncate mt-1">
                  {nextJob.customerName}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {nextJob.scheduledInstallDate}
                </p>
              </>
            ) : (
              <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Nothing queued.
              </p>
            )}
          </div>
          <button
            type="button"
            data-testid="today-pending-offers-card"
            onClick={myPending.length > 0 ? onOpenPendingOffer : undefined}
            disabled={myPending.length === 0 || !onOpenPendingOffer}
            className="rounded-md border border-border/50 p-2.5 text-left disabled:cursor-default disabled:opacity-100 enabled:hover:bg-muted/40 enabled:transition-colors enabled:cursor-pointer enabled:focus-visible:outline-none enabled:focus-visible:ring-1 enabled:focus-visible:ring-primary/30"
          >
            <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
              Pending Offers
            </p>
            <p className="text-base font-bold mt-1" data-testid="pending-offer-count">
              {myPending.length}
            </p>
            {myPending.length > 0 && (
              <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                Awaiting response <ChevronRight className="h-3 w-3" />
              </p>
            )}
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

export default TodayView;
